import { useEffect, useState } from "react";

/**
 * Single source of truth for the app's responsive boundary.
 *
 * One breakpoint: at or below this width we render the phone/small-tablet
 * layout (single column, hamburger drawer); above it, the desktop layout is
 * unchanged. Keeping the number here means every screen branches off the same
 * value instead of scattering magic widths through inline styles.
 */
export const MOBILE_MAX_WIDTH = 768;

const QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;

/**
 * `true` when the viewport is at or below {@link MOBILE_MAX_WIDTH}.
 *
 * SSR-safe (returns `false` when there's no `window`/`matchMedia`) and updates
 * live on resize and orientation change. Used to branch inline styles and
 * structural layout — the app is styled with inline `style={{}}` objects, which
 * CSS media queries can't cleanly override, so the breakpoint is read in JS.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(QUERY);
}

/**
 * `true` when the viewport is at least `px` wide. For the rare screen
 * that needs a SECOND boundary above the mobile one (the Studio editor's
 * three-column layout) — most screens should stick to useIsMobile().
 */
export function useMinWidth(px: number): boolean {
  return useMediaQuery(`(min-width: ${px}px)`);
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent): void => setMatches(e.matches);
    // Re-sync in case the viewport changed between first render and effect.
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
